// Dependencies
import axios from 'axios'

// Services
import { accessHeaders } from "../../lib/cookie"

// Utils
import { ConvertUtility as Convert } from '../utils/convert-utility'
import { ClientUtility as Client } from '../utils/client-utility'

/**
 * @typedef Province
 * @type {Object}
 * @property {String} code - Province code
 * @property {String} name - Province name
 */

/**
 * @typedef ProvinceResult
 * @type {Object}
 * @property {Number}     total - Total items
 * @property {Province[]} items - Province List
 */

/**
 * Location filter
 * @typedef LocationFilter
 * @type {Object}
 * @property {String} text      - Search Text
 * @property {String} province  - Province Code
 * @property {Number} page      - Request Page
 * @property {Number} limit     - Request Limit
 */

/**
 * Location codes
 * @typedef LocationCode
 * @type {Object}
 * @property {String} tumbonCode    - Tumbon Code
 * @property {String} amphurCode    - Amphur Code
 * @property {String} provinceCode  - Province Code
 */

/**
 * Location details
 * @typedef LocationDetail
 * @type {Object}
 * @property {String} zipCode       - Zip Code
 * @property {String} tumbonPrefix  - Tumbon Prefix ('แขวง' or 'ตำบล')
 * @property {String} tumbonName    - Tumbon Name
 * @property {String} amphurPrefix  - Amphur Prefix ('เขต' or 'อำเภอ')
 * @property {String} amphurName    - Amphur Name
 * @property {String} provinceName  - Province Code
 */

/**
 * Location full details
 * @typedef {LocationCode & LocationDetail} Location
 */

/**
 * @typedef LocationResult
 * @type {Object}
 * @property {Number}     total - Total items
 * @property {Location[]} items - Location List
 */

/**
 * @typedef AddressState
 * @type {Object}
 * @property {ProvinceResult} province - Province Result
 */

export class ProjectAddressClient {

  // Request Endpoing
  static endpoint = (
    `${process.env.ADMIN_HOST}/ruejai-backend-admin/api/project/address`
  )

  /** @type {AddressState} */
  static state = {
    province: {
      total: 0, items: []
    },
  }

  /**
   * Fetch Province List
   * @returns {Promise<ProvinceResult>} Province list
   */
  static async province() {

    // Request URL
    const url = `${this.endpoint  }/province`

    // Default Result
    let result = {
      total: 0, items: []
    }

    try {

      // State Props
      const { province } = this.state

      // Validate State
      if (province.total > 0) return province
      
      // Request Headers
      const headers = accessHeaders()

      // Request Province
      const { data } = await axios.get(
        url, { headers }
      )

      // Set Result
      if (Client.has(data)) {
        
        // Set Result
        result = data.result
        
        // Set State
        this.state.province = result
      }
    }
    catch (error) {
      
      // Error Message
      const { message } = error
      
      // Error Response
      const { errorCode } = Client.error(
        'project-address.province', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Fetch Location List
   * @param {LocationFilter} params - Filter parameters
   * @returns {Promise<LocationResult>} Location result
   */
  static async location(params) {
    
    // Request URL
    const url = this.endpoint

    // Default Result
    /** @type {LocationResult} */
    let result = {
      total: 0, items: []
    }

    try {

      // Request Headers
      const headers = accessHeaders()

      // Fetch Province
      const { data } = await axios.get(
        url, { headers, params }
      )

      // Set Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {
      
      // Error Message
      const { message } = error
      
      // Error Response
      const { errorCode } = Client.error(
        'project-address.location', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }
}